import { memo, useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { isMobile } from 'react-device-detect';

import {
  Badge,
  Button,
  Space,
  Switch,
  Table,
  Tag,
  Tooltip,
  Typography,
} from 'antd';
import type { TablePaginationConfig } from 'antd';
import type { SorterResult } from 'antd/es/table/interface';
import { PlusOutlined } from '@ant-design/icons';

import Breadcrumb from '../../../../components/common/breadcrumb';
import PageHeading from '../../../../components/common/page-heading';

import { Job } from '../../../../fsm/jobs/jobs.fsm.types';
import Layout from '../../../../components/layout';
import { matchingNotice } from '../../utils/matchingNotice';
import { customerLabel } from '../../../settings/utils/customerLabel';
import styles from './index.module.css';

import { usePermissions } from '../../../../context/permission.context';
import { User } from '../../../../fsm/users';

const orderMapping = {
  ascend: 'ASC',
  descend: 'DESC',
};

interface Props {
  user?: User;
  data: Array<Job>;
  isLoading: boolean;
  totalElements: number;
  totalPages: number;
  onFetchJobs: (
    pageNumber: number,
    pageSize: number,
    sort?: string,
    order?: string,
    status?: string,
    includeSynthetic?: boolean,
  ) => void;
}

/*
 * The "Dashboard" crumb was removed when there was no dashboard route to point
 * at -- it named a page that did not exist. The dashboard now exists, so the
 * crumb returns WITH an href, which is the version that earns its place: it
 * goes somewhere, rather than asserting a hierarchy nobody can navigate.
 */
const breadcrumbItems = [
  {
    title: <Link to="/dashboard">Dashboard</Link>,
  },
  {
    title: 'Job list',
  },
];

const JobList = ({
  user,
  data,
  isLoading,
  totalElements,
  totalPages,
  onFetchJobs,
}: Props) => {
  const [pageNumber] = useState<number>(1);
  const [pageSize] = useState<number>(isMobile ? 6 : 10);
  const [sortOrder] = useState<string | undefined>('DESC');
  const [sortField] = useState<string | undefined>('id');
  const {
    canAddNewJob,
    canSeeJobRecruiterColumn,
    canSeeJobHiringManagerColumn,
    canSeeJobCustomerNameColumn,
    canSeeJobCompanyColumn,
  } = usePermissions();

  // Super Admin only (the same view that shows the Customer name column): jobs of customers flagged
  // synthetic - test, E2E and demo tenants - are hidden by default so real work isn't buried in them.
  const canSeeSyntheticToggle = canSeeJobCustomerNameColumn;
  const [includeSynthetic, setIncludeSynthetic] = useState(false);
  // What the table last asked for, so flipping the toggle keeps the person's sort and status filter.
  const lastQuery = useRef<{ sort?: string; order?: string; status?: string }>({
    sort: sortField,
    order: sortOrder,
  });

  useEffect(() => {
    onFetchJobs(pageNumber, pageSize, sortField, sortOrder);
  }, []);

  const handleIncludeSyntheticChange = useCallback(
    (checked: boolean) => {
      setIncludeSynthetic(checked);
      onFetchJobs(
        1,
        pageSize,
        lastQuery.current.sort,
        lastQuery.current.order,
        lastQuery.current.status,
        checked,
      );
    },
    [pageSize, onFetchJobs],
  );

  const handleTableChange = useCallback(
    (
      pagination: TablePaginationConfig,
      // eslint-disable-next-line  @typescript-eslint/no-explicit-any
      filters: Record<string, any>,
      sorter: SorterResult<Job> | SorterResult<Job>[],
    ) => {
      const currentSorter = Array.isArray(sorter) ? sorter[0] : sorter;
      const sort = (currentSorter.field as string) || sortField;
      const order =
        orderMapping[currentSorter.order as keyof typeof orderMapping] ||
        sortOrder;
      const status = filters?.status?.length
        ? filters?.status.join(',')
        : undefined;
      lastQuery.current = { sort, order, status };

      onFetchJobs(
        (pagination.current as number) || pageNumber,
        pageSize,
        sort,
        order,
        status,
        includeSynthetic,
      );
    },
    [pageSize, sortField, sortOrder, pageNumber, onFetchJobs, includeSynthetic],
  );

  const columns = [
    {
      title: 'Avrio ID',
      dataIndex: 'id',
      key: 'id',
      width: '8%',
      sorter: true,
    },
    {
      /*
       * The customer's own ATS identifier for this listing, so their staff
       * can cross-reference it in the system they already use. Naming note
       * for anyone tempted to "fix" it: this refers to the CUSTOMER's ATS.
       * Avrio is not one, and CLAUDE.md's prohibition is on describing Avrio
       * that way, not on referencing a customer's.
       *
       * NOT WIRED, AND DELIBERATELY NOT FAKED. `atsId` exists in neither this
       * app's Job type nor anywhere in avrio-backend -- searched both, with a
       * control to prove the search worked -- so every row has always been
       * blank. Nachi's call is to keep the column as a placeholder until the
       * customer job-feed integration lands.
       *
       * A placeholder here means a VISIBLE "not connected", not invented
       * identifiers. A plausible-looking ID is something a recruiter would
       * try to paste into their ATS, and it would not resolve; fabricating
       * one would make this column assert a cross-reference that does not
       * exist, which is the same defect as ticking every skill or scoring a
       * dimension the product does not score.
       *
       * The nearest real field is JobEntity.externalId in avrio-backend-jobs,
       * populated from the TheirStack feed -- but TheirStack is an
       * aggregation source Avrio pulls from, not a per-customer upload, so
       * that may be TheirStack's posting ID rather than the customer's. That
       * question belongs to whoever owns that ingestion.
       */
      title: 'ATS ID',
      dataIndex: 'atsId',
      key: 'atsId',
      width: '8%',
      render: (atsId?: string) =>
        atsId ? (
          <span className={styles.nowrap}>{atsId}</span>
        ) : (
          <Tooltip title="Connected once the customer's job feed integration lands. No ATS identifier is stored for this listing yet.">
            {/* "Not connected" reads as a broken integration rather than an
                empty field -- matches the dashboard's own "Best fit —"
                convention for a value that simply isn't there yet. */}
            <span className={styles.atsPlaceholder}>&mdash;</span>
          </Tooltip>
        ),
    },
    ...(canSeeJobCompanyColumn
      ? [
          {
            title: 'Company',
            dataIndex: 'employerName',
            key: 'employerName',
            render: (employerName?: string) => (
              <Typography>{employerName || 'N/A'}</Typography>
            ),
          },
        ]
      : []),
    {
      title: 'Job title',
      dataIndex: 'title',
      key: 'title',
      width: '24%',
      // A paused job is otherwise indistinguishable from one with no matches
      // yet, so the list says so (matching limits, 2026-10-02).
      render: (title: string, job: Job) => {
        const notice = matchingNotice(job);
        return (
          <Space size={6} wrap>
            <span>{title}</span>
            {notice?.badge && (
              <Tag color={notice.tone === 'error' ? 'error' : 'warning'}>
                {notice.badge}
              </Tag>
            )}
          </Space>
        );
      },
    },
    ...(canSeeJobCustomerNameColumn
      ? [
          {
            title: 'Customer name',
            dataIndex: 'customerName',
            key: 'customerName',
            render: (name: string, job: Job) =>
              name && job.customerIsSynthetic
                ? customerLabel({ name, isSynthetic: true })
                : name,
          },
        ]
      : []),
    ...(canSeeJobRecruiterColumn
      ? [
          {
            title: 'Recruiter',
            dataIndex: 'recruiterName',
            key: 'recruiter',
          },
        ]
      : []),
    ...(canSeeJobHiringManagerColumn
      ? [
          {
            title: 'Hiring manager',
            dataIndex: 'hiringManagerName',
            key: 'hiringManagerName',
          },
        ]
      : []),
    {
      title: 'Posted date',
      dataIndex: 'postedAt',
      key: 'postedAt',
      render: (postedAt?: number) => (
        <div className={styles.nowrap}>
          {postedAt
            ? new Intl.DateTimeFormat('en-US', {
                year: 'numeric',
                month: 'short',
                day: 'numeric',
                hour: 'numeric',
                minute: 'numeric',
              }).format(new Date(postedAt * 1000))
            : 'No date'}
        </div>
      ),
      sorter: true,
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      filterMultiple: false,
      filters: [
        { text: 'Open', value: 'OPEN' },
        { text: 'Closed', value: 'CLOSED' },
      ],
      /*
       * JobStatus is exactly OPEN | CLOSED -- checked against the enum in
       * avrio-backend's openapi.yaml, not assumed. So these two branches are
       * exhaustive and no fallback is needed, which is NOT the case on the
       * candidate status column, where four branches covered nine states and a
       * HIRED candidate rendered identically to a brand-new one (fixed in #28).
       * If JobStatus ever gains a third value, this renders an empty cell.
       */
      render: (status: string) => (
        <Space>
          {status === 'OPEN' && (
            <>
              <Badge status="success" />{' '}
              <span className={styles.statusLabel}>Open</span>
            </>
          )}
          {status === 'CLOSED' && (
            <>
              <Badge status="error" />{' '}
              <span className={styles.statusLabel}>Closed</span>
            </>
          )}
        </Space>
      ),
    },
    {
      title: 'Actions',
      dataIndex: 'listing',
      key: 'listing',
      render: (_a: unknown, { id }: Job) => (
        <Space size={20} className={styles.actions}>
          <Link className={styles.nowrap} to={id.toString()}>
            Job post
          </Link>
          <Link className={styles.nowrap} to={`${id.toString()}/candidates`}>
            Candidates
          </Link>
        </Space>
      ),
    },
  ];

  return (
    <Layout user={user}>
      <Breadcrumb
        items={breadcrumbItems}
        renderRight={
          canAddNewJob && (
            <Button type="primary" shape="round" icon={<PlusOutlined />}>
              <Link to="create">Add new job</Link>
            </Button>
          )
        }
      />
      <div className={styles.wrapper}>
        <PageHeading
          title="Job list"
          count={{ value: totalElements, singular: 'job', plural: 'jobs' }}
        />
        {canSeeSyntheticToggle && (
          <Space className={styles.syntheticToggle} size={8}>
            <Switch
              checked={includeSynthetic}
              onChange={handleIncludeSyntheticChange}
              aria-label="Include test customers' jobs"
            />
            <span>Include test (synthetic) customers&rsquo; jobs</span>
          </Space>
        )}
        <Table
          // A new key starts the table on page 1, which is where a toggle refetches to.
          key={includeSynthetic ? 'with-synthetic' : 'real-only'}
          dataSource={data}
          columns={columns}
          loading={isLoading}
          scroll={{ x: 800 }}
          pagination={
            totalPages > 1 && {
              total: totalElements,
              pageSize: pageSize,
              showSizeChanger: false,
            }
          }
          onChange={handleTableChange}
        />
      </div>
    </Layout>
  );
};

export default memo(JobList);
